import { useMemo, useState } from 'react';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { ASSETS, NETWORKS, type AssetCode, type AssetNetwork, type WalletDTO } from '@onyx/shared';
import { useLang, useT } from '../lib/i18n';
import { cryptoParts, displayDigits, fiatParts } from '../lib/format';
import { useFiat } from '../lib/app';
import { AssetChip } from './AssetChip';
import { Money, Row } from './ui';
import { NetworkMark } from './NetworkMark';
import { standardTag } from '../lib/networks';

/** Assets ordered by what the user holds first, then by the registry's popularity order. */
export function useAssetOrder(wallet: WalletDTO | undefined, fiat: string) {
  return useMemo(() => {
    const value = (code: AssetCode) => {
      const b = wallet?.balances.find((x) => x.asset === code);
      const p = wallet?.prices[code]?.[fiat as 'USD'] ?? 0;
      return b ? Number(b.available) * p : 0;
    };
    return [...ASSETS].sort((a, b) => value(b.code) - value(a.code) || ASSETS.indexOf(a) - ASSETS.indexOf(b));
  }, [wallet, fiat]);
}

export function AssetList({
  wallet,
  onPick,
  filter,
  showSearch = true,
}: {
  wallet: WalletDTO | undefined;
  onPick: (code: AssetCode) => void;
  filter?: (code: AssetCode) => boolean;
  showSearch?: boolean;
}) {
  const t = useT();
  const lang = useLang();
  const fiat = useFiat();
  const [q, setQ] = useState('');
  const ordered = useAssetOrder(wallet, fiat);
  const list = ordered.filter(
    (a) => (!filter || filter(a.code)) && (!q || a.code.toLowerCase().includes(q.toLowerCase()) || a.name.toLowerCase().includes(q.toLowerCase())),
  );
  return (
    <div className="asset-list">
      {showSearch && (
        <label className="search">
          <MagnifyingGlass size={18} aria-hidden="true" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t['pick.search']} aria-label={t['pick.search']} autoComplete="off" />
        </label>
      )}
      <div className="rows">
        {list.map((a) => {
          const bal = wallet?.balances.find((b) => b.asset === a.code)?.available ?? '0';
          const price = wallet?.prices[a.code]?.[fiat] ?? 0;
          const n = Number(bal);
          return (
            <Row
              key={a.code}
              onClick={() => onPick(a.code)}
              leading={<AssetChip asset={a.code} size={40} />}
              title={a.code}
              subtitle={a.networks.length > 1 ? `${a.name} · ${t['home.networks'](a.networks.length)}` : a.name}
              trailing={n > 0 ? <Money parts={cryptoParts(bal, displayDigits(n, a.stable), lang)} unit={a.code} size="md" /> : undefined}
              trailingSub={n > 0 ? <Money parts={fiatParts(n * price, fiat, lang)} size="sm" /> : undefined}
            />
          );
        })}
      </div>
    </div>
  );
}

export function NetworkList({ asset, onPick, mode }: { asset: AssetCode; onPick: (n: AssetNetwork) => void; mode: 'deposit' | 'withdraw' }) {
  const t = useT();
  const lang = useLang();
  const def = ASSETS.find((a) => a.code === asset)!;
  return (
    <div className="rows">
      {def.networks.map((n) => {
        const net = NETWORKS[n.network];
        const fee = cryptoParts(n.withdrawFee, 8, lang);
        const min = cryptoParts(mode === 'deposit' ? n.minDeposit : n.minWithdraw, 8, lang);
        return (
          <Row
            key={n.network}
            onClick={() => onPick(n)}
            leading={<NetworkMark network={n.network} />}
            title={
              <>
                {net.name}
              </>
            }
            subtitle={[standardTag(n.standard), t['pick.minutes'](net.minutes), `${t['pick.min']} ${min.int}${min.sep}${min.frac} ${asset}`].filter(Boolean).join(' · ')}
            trailing={mode === 'withdraw' ? <span className="num">{`${fee.int}${fee.sep}${fee.frac} ${asset}`}</span> : undefined}
            trailingSub={mode === 'withdraw' ? t['pick.fee'] : undefined}
            chevron
          />
        );
      })}
    </div>
  );
}
